import React, { useMemo } from 'react';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { DataSetCompositeRecipeRenameColumnsConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeRenameColumnsConfigComponent';
import { DataSetCompositeRecipeValidateCommonModelConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeValidateCommonModelConfigComponent';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { DataSetCompositeRecipeAiInterviewerConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeAIInterviewerConfigComponent';
import { DataSetCompositeRecipeEmployeeCandidateMatchingConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeEmployeeCandidateMatchingConfigComponent';
import { UseGenerateReportsInsightsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-generate-reports-insights';
import { DataSetCompositeRecipeMergeResumeParserConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeMergeResumeParserConfigComponent';
import { DataSetCompositeRecipeResumeAddressParserConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeResumeAddressParserConfigComponent';
import { DataSetCompositeRecipeCommuteTimesConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeCommuteTimesConfigComponent';
import { DataSetCompositeRecipeMergeFilteringConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeMergeFilteringConfigComponent';
import { DataSetCompositeRecipeResumeFetcherConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeResumeFetcherConfigComponent';
import { DataSetCompositeRecipeResumeParserConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeResumeParserConfigComponent';
import { DataSetCompositeRecipeDagsterSourceConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDagsterSourceConfigComponent';

export const DataSetCompositeRecipeConfigurationComponent = ({
  organizationId,
  databaseId,
  recipe,
  setRecipe,
  generateInsightsPayload,
  taggedDataSetReferences,
}: {
  organizationId: string;
  databaseId: string;
  recipe: CompositeDataSetRecipe | null;
  setRecipe: (recipe: CompositeDataSetRecipe | null) => void;
  generateInsightsPayload: UseGenerateReportsInsightsPayload;
  taggedDataSetReferences: TaggedDataSetReference[];
}) => {
  const ui = useMemo(() => {
    if (!recipe) {
      return null;
    }

    switch (recipe.type) {
      case 'rename-columns':
        return (
          <DataSetCompositeRecipeRenameColumnsConfigComponent
            config={recipe}
            setConfig={setRecipe}
            insightsPayload={generateInsightsPayload}
          />
        );
      case 'common-model-validation':
        return (
          <DataSetCompositeRecipeValidateCommonModelConfigComponent
            config={recipe}
            setConfig={setRecipe}
            insightsPayload={generateInsightsPayload}
          />
        );
      case 'ai-interviewer':
        return (
          <DataSetCompositeRecipeAiInterviewerConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
          />
        );
      case 'employee-candidate-matching':
        return (
          <DataSetCompositeRecipeEmployeeCandidateMatchingConfigComponent
            config={recipe}
            setConfig={setRecipe}
            insightsPayload={generateInsightsPayload}
          />
        );
      case 'merge-resume-parsing':
        return (
          <DataSetCompositeRecipeMergeResumeParserConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
          />
        );
      case 'resume-address-parser':
        return (
          <DataSetCompositeRecipeResumeAddressParserConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
          />
        );
      case 'commute-times':
        return (
          <DataSetCompositeRecipeCommuteTimesConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
            insightsPayload={generateInsightsPayload}
          />
        );
        case 'merge-filtering':
          return <DataSetCompositeRecipeMergeFilteringConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
          />;
      case 'resume-fetcher':
        return <DataSetCompositeRecipeResumeFetcherConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
            taggedDataSetReferences={taggedDataSetReferences}
          />;
      case 'resume-parser':
        return <DataSetCompositeRecipeResumeParserConfigComponent
            organizationId={organizationId}
            databaseId={databaseId}
            config={recipe}
            setConfig={setRecipe}
            taggedDataSetReferences={taggedDataSetReferences}
          />;
      case 'dagster-source':
        return <DataSetCompositeRecipeDagsterSourceConfigComponent
            organizationId={organizationId}
            config={recipe}
            setConfig={setRecipe}
          />;
      default:
        throw guardExhaustive(recipe);
    }
  }, [recipe, setRecipe, generateInsightsPayload, organizationId, databaseId, taggedDataSetReferences]);

  return (
    <Box bg={'gray-200'} p={'md'} rounded={'lg'}>
      {ui}
    </Box>
  );
};
